import React from 'react';
import { ChangeEvent, useEffect } from 'react';
import Button from 'src/components/Button';
import FontAwesomeIcon from 'src/components/Icon';
import { generateRandomNumber } from 'src/helpers/CommonHelpers';
import { KeyValuePair } from 'src/components/SuitesTable/CustomHooks/useKeyPickups';
import './KeyValuePairs.scss';

interface KeyValuePairsProps {
  pairs: KeyValuePair[];
  onChange: (pairs: KeyValuePair[]) => void;
  onValidationChange?: (hasErrors: boolean) => void;
  keyLabel?: string;
  valueLabel?: string;
  keyPlaceholder?: string;
  valuePlaceholder?: string;
  addButtonLabel?: string;
  className?: string;
}

// Modular, re-usable UI component we can also eventually integrate into: src/pages/Suite/Suite.jsx once we refactor that.
const KeyValuePairs = ({
  pairs,
  onChange,
  onValidationChange,
  keyLabel = 'Key',
  valueLabel = 'Value',
  keyPlaceholder = 'Label',
  valuePlaceholder = 'Description',
  addButtonLabel = '+ Add Details',
  className = '',
}: KeyValuePairsProps) => {
  const handleAddKeyValuePair = () => {
    const newKeyValuePair: KeyValuePair = {
      id: generateRandomNumber().toString(),
      key: '',
      value: '',
    };

    onChange([...pairs, newKeyValuePair]);
  };

  const handleRemoveKeyValuePair = (id: string) => {
    const updatedKeyValuePairs: KeyValuePair[] = pairs.filter(
      (pair: KeyValuePair) => pair.id !== id
    );

    onChange(updatedKeyValuePairs);
  };

  const handleKeyValuePairChange = (
    id: string,
    field: 'key' | 'value',
    value: string
  ) => {
    const updatedKeyValuePairs: KeyValuePair[] = pairs.map(
      (pair: KeyValuePair) => {
        if (pair.id === id) {
          const updatedPair: KeyValuePair = { ...pair, [field]: value };
          return updatedPair;
        }

        return pair;
      }
    );

    onChange(updatedKeyValuePairs);
  };

  const isKeyValuePairEmpty = (pair: KeyValuePair) => {
    return !pair.key.trim() || !pair.value.trim();
  };

  // Check if there are any partially filled pairs (validation errors)
  const hasValidationErrors = (): boolean => {
    const hasInvalidPairs: boolean =
      pairs.length > 0 &&
      pairs.some((pair: KeyValuePair) => {
        const hasKey: boolean = pair.key.trim().length > 0;
        const hasValue: boolean = pair.value.trim().length > 0;
        return (
          (hasKey && !hasValue) ||
          (!hasKey && hasValue) ||
          (!hasKey && !hasValue)
        );
      });

    return hasInvalidPairs;
  };

  // Notify parent whenever validation state changes
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(hasValidationErrors());
    }
  }, [pairs, onValidationChange]);

  return (
    <div className={`key-value-pairs ${className}`}>
      <div className="key-value-pairs-section">
        {pairs.map((pair: KeyValuePair, index: number) => (
          <div key={pair.id} className="key-value-pair-row">
            <div className="key-value-pair-container">
              <div className="key-value-pair-inputs">
                <input
                  type="text"
                  className={`key-value-text-input key-value-key-input ${
                    isKeyValuePairEmpty(pair) ? 'key-value-input-error' : ''
                  }`}
                  value={pair.key}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    handleKeyValuePairChange(pair.id, 'key', e.target.value)
                  }
                  placeholder={keyPlaceholder}
                />
                <input
                  type="text"
                  className={`key-value-text-input key-value-value-input ${
                    isKeyValuePairEmpty(pair) ? 'key-value-input-error' : ''
                  }`}
                  value={pair.value}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    handleKeyValuePairChange(pair.id, 'value', e.target.value)
                  }
                  placeholder={valuePlaceholder}
                />
              </div>
              {isKeyValuePairEmpty(pair) &&
                (pair.key.length > 0 || pair.value.length > 0) && (
                  <div className="key-value-error-message">
                    Both {keyLabel.toLowerCase()} and {valueLabel.toLowerCase()}{' '}
                    must be filled
                  </div>
                )}
            </div>
            <button
              type="button"
              className="key-value-remove-button"
              onClick={() => handleRemoveKeyValuePair(pair.id)}
            >
              <FontAwesomeIcon glyph="trash" weight="light" />
            </button>
          </div>
        ))}
        <Button
          color="teal"
          size="small"
          label={addButtonLabel}
          onClick={handleAddKeyValuePair}
        />
      </div>
    </div>
  );
};

export default KeyValuePairs;
